import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import {
	SOFTPHONE_DEFAULT_FALLBACK_KIND,
	SOFTPHONE_DEFAULT_RING_STRATEGY,
	type SoftphoneGroupRoleFallbackKindModel,
	type SoftphoneRingStrategyModel,
} from '../../constants/softphone-ring-strategies';
import { BaseEntity } from './base.entity';
import { GroupEntity } from './group.entity';

/**
 * Per-Group softphone routing config — overrides the tenant default
 * ring strategy when this Group is the destination of an inbound
 * number or a transfer. One row per Group; `fkGroupId` unique.
 *
 * Resolution chain (`SoftphoneRingStrategyResolverService`):
 *   1. This row's `ringStrategy` — wins if present
 *   2. Tenant default — `InstanceSettingEntity` keyed by
 *      `softphone.defaultRingStrategy`
 *   3. Hard-coded fallback — `simultaneous` (always-coverage)
 *
 * `roundRobinNextIndex` is only meaningful when `ringStrategy`
 * is `'round-robin'`. It's the modulo-position into the sorted
 * member list for the next ring attempt; advanced after each
 * attempt so successive calls fan out evenly.
 *
 * Why a separate table (vs columns on `GroupEntity`): keeps Group
 * a pure org-chart entity and avoids polluting it with feature-
 * specific (softphone) config. Mirrors the
 * `SoftphoneNumberEntity` ↔ `SoftphoneNumberConfigEntity` split.
 */
@Index('softphone_group_config_group_pk', ['fkGroupId'], { unique: true })
@Entity('softphone_group_config')
export class SoftphoneGroupConfigEntity extends BaseEntity {
	@Column('int', { name: 'fk_group__id', unsigned: true })
	public fkGroupId!: number;

	@Column('varchar', {
		name: 'ringStrategy',
		length: 32,
		default: () => `'${SOFTPHONE_DEFAULT_RING_STRATEGY}'`,
	})
	public ringStrategy!: SoftphoneRingStrategyModel;

	@Column('int', {
		name: 'roundRobinNextIndex',
		unsigned: true,
		default: 0,
	})
	public roundRobinNextIndex!: number;

	/**
	 * How long each individual ring attempt waits before the
	 * orchestrator advances. Only meaningful for `round-robin` and
	 * `longest-idle` (simultaneous fans out in parallel — no per-rep
	 * timeout, the whole batch shares a standard ring window).
	 *
	 * Default 5s — short enough that auto-advance feels snappy
	 * (typical reps need ~3s to decide whether to answer a popup);
	 * long enough that a rep glancing at their screen has time to
	 * accept. Tunable per-destination since some teams want longer
	 * (high-stakes calls) or shorter (very large rotations where
	 * cycling through everyone fast matters more).
	 */
	@Column('int', {
		name: 'attemptTimeoutSeconds',
		unsigned: true,
		default: 5,
	})
	public attemptTimeoutSeconds!: number;

	/**
	 * No-answer fallback choice when ALL members of this Group exhaust
	 * (DD.4.E). Defaults to `'none'` — a freshly-created group hangs
	 * up rather than recording into a voicemail with no greeting.
	 *
	 * Forwarding kinds (`forwardToX`) are NOT valid here — Group /
	 * Role configs only accept `none` / `voicemail` / `aiAgent`. The
	 * "only direct rings get forwarded" rule keeps the fallback graph
	 * shallow: a Group's tail is always one of these three, never
	 * another forward.
	 */
	@Column('varchar', {
		name: 'fallbackKind',
		length: 24,
		default: () => `'${SOFTPHONE_DEFAULT_FALLBACK_KIND}'`,
	})
	public fallbackKind!: SoftphoneGroupRoleFallbackKindModel;

	/**
	 * Set when `fallbackKind = 'aiAgent'` — which AI agent picks up
	 * once the group's ring exhausts.
	 */
	@Column('int', {
		name: 'fallbackAiAgentId',
		unsigned: true,
		nullable: true,
	})
	public fallbackAiAgentId!: number | null;

	/**
	 * URL of the Group's recorded voicemail greeting (Twilio Assets
	 * URL, populated by DD.4.G's audio-capture flow). Null = use
	 * default TTS greeting. Only meaningful when
	 * `fallbackKind = 'voicemail'`.
	 */
	@Column('varchar', {
		name: 'voicemailGreetingUrl',
		length: 512,
		nullable: true,
	})
	public voicemailGreetingUrl!: string | null;

	@ManyToOne(() => GroupEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_group__id', referencedColumnName: 'id' }])
	public fkGroup!: GroupEntity;
}
